import '../../index.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, ChevronDown, LogOut, Settings, Loader2, Layers } from 'lucide-react';
import { useAuth } from '../../context/authContext';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';
import { buttonVariants } from '../ui/button';
import { cn } from '../../utils';
import {
    getActiveOrganisationUuid,
    organisationIdsMatch,
    isAllOrganisationsSelected,
    ALL_ORGANISATIONS_ID,
    ALL_ORGANISATIONS_NAME,
} from '../../utils/organisation';
import { getUserDetails } from '../../services/user.service';
import { getMsalProfilePhoto } from '../../services/auth.service';

const PREVIEW_ORGANISATION = {
    id: 'preview-organisation',
    name: 'Northern Finance Group',
    isPreview: true,
};

const withPreviewOrganisation = (organisations) => {
    if (!organisations || organisations.length === 0) return [];
    if (organisations.length > 1) return organisations;
    if (process.env.NODE_ENV === 'development') {
        return [...organisations, PREVIEW_ORGANISATION];
    }
    return organisations;
};

const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const OrganisationRef = ({ id }) => {
    if (!id) return null;
    return (
        <>
            <span className="hidden sm:block h-6 w-px bg-slate-200 shrink-0" aria-hidden />
            <span
                className="hidden sm:block text-[10px] text-slate-400 font-mono tracking-wide max-w-[360px] shrink-0"
                title={id}
            >
                {id}
            </span>
        </>
    );
};

const Header = () => {
    const { user, setUser, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [profilePhoto, setProfilePhoto] = useState(user.picture || '');
    const [logoutOpen, setLogoutOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);

    const organisations = useMemo(
        () => withPreviewOrganisation(user.organisations),
        [user.organisations]
    );

    const showSwitcher = organisations.length > 1;
    const allOrgsActive = isAllOrganisationsSelected(user);

    const organisationRef = useMemo(
        () => getActiveOrganisationUuid(user),
        [user]
    );

    useEffect(() => {
        let cancelled = false;

        const loadProfilePhoto = async () => {
            if (user.picture) {
                setProfilePhoto(user.picture);
                return;
            }

            const photoUrl = await getMsalProfilePhoto();
            if (cancelled || !photoUrl) return;

            setProfilePhoto(photoUrl);
            setUser((prev) => (prev.picture ? prev : { ...prev, picture: photoUrl }));
        };

        loadProfilePhoto();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleSelectAllOrganisations() {
        setUser({
            ...user,
            organisationId: ALL_ORGANISATIONS_ID,
            organisationName: ALL_ORGANISATIONS_NAME,
        });
        // Admin pages are hidden in this mode, so always return to payments to
        // avoid being stranded on a route that no longer exists.
        navigate('/payments');
    }

    async function handleChangeOrganisation(organisation) {
        if (organisation.isPreview) {
            setUser({
                ...user,
                organisationId: organisation.id,
                organisationName: organisation.name,
            });
            if (location.pathname.includes('/payments')) {
                navigate('/payments');
            }
            return;
        }

        const userDetails = await getUserDetails(user.token, organisation.id, user.id);
        if (userDetails?.status === 200) {
            setUser({
                ...user,
                ...userDetails.data.data,
                organisationId: organisation.id,
                organisationName: organisation.name,
            });
            if (location.pathname.includes('/payments')) {
                navigate('/payments');
            }
        }
    }

    const avatarSrc = profilePhoto || user.picture;

    const renderAvatar = (sizeClass) =>
        avatarSrc ? (
            <img
                src={avatarSrc}
                alt={user.name}
                className={cn(sizeClass, 'rounded-full object-cover border-2 border-slate-100 shadow-sm')}
            />
        ) : (
            <span
                className={cn(sizeClass, 'flex shrink-0 items-center justify-center rounded-full bg-[#2E2E2E] text-xs font-semibold text-white')}
                title={user.name}
            >
                {getInitials(user.name)}
            </span>
        );

    const handleLogout = () => {
        setLoggingOut(true);
        // Brief, deliberate pause so the "signing out" state is perceptible before the
        // session is cleared and the user is returned to the login screen.
        setTimeout(() => {
            logout();
        }, 500);
    };

    return (
        <div className="border-b bg-white border-slate-200 shadow-sm shrink-0">
            <div className="flex flex-row justify-between items-center px-6 py-4">
                <div className="flex items-center gap-4 min-w-0">
                    {showSwitcher ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-[#2E2E2E] shadow-sm hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-200/80"
                                >
                                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 border border-slate-200">
                                        <Building2 className="h-4 w-4 text-slate-500" />
                                    </span>
                                    <span className="flex flex-col items-start leading-tight">
                                        <span className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
                                            Organisation
                                        </span>
                                        <span className="max-w-[220px] truncate font-semibold">
                                            {user.organisationName}
                                        </span>
                                    </span>
                                    <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0 ml-1" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-72 p-2">
                                <DropdownMenuLabel className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase px-2 py-1.5">
                                    Switch organisation
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator className="my-1" />
                                <DropdownMenuItem
                                    className="flex items-center gap-3 rounded-md px-2 py-2.5 cursor-pointer focus:bg-slate-50"
                                    onClick={handleSelectAllOrganisations}
                                >
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-yavrio-green/10 border border-yavrio-green/20">
                                        <Layers className="h-4 w-4 text-yavrio-green" />
                                    </span>
                                    <span className="flex flex-col min-w-0 flex-1">
                                        <span className={`truncate ${allOrgsActive ? 'font-semibold text-[#2E2E2E]' : 'text-slate-700'}`}>
                                            {ALL_ORGANISATIONS_NAME}
                                        </span>
                                        <span className="text-[10px] text-slate-400">Payments across every organisation</span>
                                    </span>
                                    {allOrgsActive && (
                                        <Check className="h-4 w-4 text-yavrio-green shrink-0" />
                                    )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="my-1" />
                                {organisations.map((organisation) => {
                                    const isActive = organisationIdsMatch(user.organisationId, organisation.id);
                                    return (
                                        <DropdownMenuItem
                                            key={organisation.id}
                                            className="flex items-center gap-3 rounded-md px-2 py-2.5 cursor-pointer focus:bg-slate-50"
                                            onClick={() => handleChangeOrganisation(organisation)}
                                        >
                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-slate-100 border border-slate-200">
                                                <Building2 className="h-4 w-4 text-slate-500" />
                                            </span>
                                            <span className="flex flex-col min-w-0 flex-1">
                                                <span className={`truncate ${isActive ? 'font-semibold text-[#2E2E2E]' : 'text-slate-700'}`}>
                                                    {organisation.name}
                                                </span>
                                                {organisation.isPreview && (
                                                    <span className="text-[10px] text-slate-400">Preview organisation</span>
                                                )}
                                            </span>
                                            {isActive && (
                                                <Check className="h-4 w-4 text-yavrio-green shrink-0" />
                                            )}
                                        </DropdownMenuItem>
                                    );
                                })}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <div className="inline-flex items-center gap-3 min-w-0">
                            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 border border-slate-200">
                                <Building2 className="h-4 w-4 text-slate-500" />
                            </span>
                            <h3 className="m-0 font-semibold text-yavrio-blue max-w-[280px] truncate">
                                {user.organisationName}
                            </h3>
                        </div>
                    )}
                    <OrganisationRef id={organisationRef} />
                </div>
                <div className="flex items-center gap-3">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className="inline-flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-200/80"
                            >
                                <span className="hidden sm:block text-sm font-medium text-[#2E2E2E] max-w-[180px] truncate mr-0.5">
                                    {user.name || user.email}
                                </span>
                                {renderAvatar('h-9 w-9')}
                                <ChevronDown className="hidden sm:block h-4 w-4 text-slate-400 shrink-0" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-64 p-2">
                            <div className="flex items-center gap-3 px-2 py-2">
                                {renderAvatar('h-10 w-10')}
                                <div className="min-w-0">
                                    <p className="m-0 text-sm font-semibold text-[#2E2E2E] truncate">
                                        {user.name || 'Account'}
                                    </p>
                                    <p className="m-0 text-xs text-slate-400 truncate" title={user.email}>
                                        {user.email}
                                    </p>
                                </div>
                            </div>
                            {user.role && (
                                <div className="px-2 pb-1.5">
                                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                        {user.role}
                                    </span>
                                </div>
                            )}
                            <DropdownMenuSeparator className="my-1" />
                            <DropdownMenuItem
                                className="flex items-center gap-2 rounded-md px-2 py-2 cursor-pointer focus:bg-slate-50"
                                onClick={() => navigate('/settings')}
                            >
                                <Settings className="h-4 w-4 text-slate-500" />
                                <span>Settings</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="my-1" />
                            <DropdownMenuItem
                                className="flex items-center gap-2 rounded-md px-2 py-2 cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600"
                                onSelect={(e) => {
                                    e.preventDefault();
                                    setLogoutOpen(true);
                                }}
                            >
                                <LogOut className="h-4 w-4" />
                                <span>Log out</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <AlertDialog open={logoutOpen} onOpenChange={(open) => !loggingOut && setLogoutOpen(open)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Log out of the portal?</AlertDialogTitle>
                    </AlertDialogHeader>
                    <AlertDialogDescription>
                        You&apos;ll be signed out of the Payment Authorisation Portal and returned to the login
                        screen. Any unsaved changes will be lost.
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={loggingOut}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleLogout();
                            }}
                            disabled={loggingOut}
                            className={cn(buttonVariants({ variant: 'destructive' }), 'gap-2')}
                        >
                            {loggingOut ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Signing out...
                                </>
                            ) : (
                                <>
                                    <LogOut className="h-4 w-4" />
                                    Log out
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default Header;
